<template>
  <div class="animal-habitat-game">
    <h1>Feed the Animals</h1>
    <LevelComplete v-if="gameOver" />

    <div
      v-if="currentAnimal"
      class="game-container"
    >
      <!-- Scene -->
      <div class="scene">
        <!-- Habitat -->
        <div
          class="habitat"
          :style="{
            backgroundImage: 'url(' + currentAnimal.habitatImage + ')',
          }"
        >
          <!-- If currentStep >= steps.length, show animal inside habitat -->
          <div
            v-if="currentAnimal.currentStep >= currentAnimal.steps.length"
            class="animal-in-habitat"
          >
            {{ currentAnimal.animalEmoji }}
          </div>
        </div>
        <!-- Steps and Animal -->
        <div class="steps-container">
          <div class="steps">
            <div
              v-for="(step, index) in currentAnimal.steps"
              :key="index"
              class="step"
              :data-step-index="index"
              @dragover.prevent
              @drop="onDropOnStep($event, step)"
            >
              <!-- If animal is at this step -->
              <div
                v-if="currentAnimal.currentStep === index"
                class="animal"
                @click="speakOutLoud(currentAnimal.animalName)"
              >
                {{ currentAnimal.animalEmoji }}
              </div>
              <!-- Step Content -->
              <div class="step-content">
                <!-- If food is placed and not yet eaten, show it -->
                <div v-if="step.food && !step.foodEaten">
                  <span style="font-size: 3rem">{{ step.food.foodEmoji }}</span>
                </div>
                <!-- Else show placeholder -->
                <div
                  v-else
                  class="placeholder"
                >
                  <!-- Empty gray square -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Message Display -->
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>

      <!-- Food Items -->
      <div class="foods">
        <div
          v-for="food in availableFoods"
          :key="food.id"
          class="food"
          draggable="true"
          @dragstart="onDragStart($event, food)"
          @click="speakOutLoud(food.foodName)"
          @touchstart="onTouchStartFood($event, food)"
          @touchmove="onTouchMoveFood($event)"
          @touchend="onTouchEndFood($event)"
        >
          <span style="font-size: 3rem">{{ food.foodEmoji }}</span>
        </div>
      </div>
    </div>

    <!-- Floating Drag Image -->
    <div
      v-if="isDragging"
      :style="dragImageStyle"
      class="drag-image"
    >
      <span style="font-size: 3rem">{{ dragImage }}</span>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      animals: [
        {
          animalName: "Rabbit",
          animalEmoji: "🐇",
          foodName: "Carrot",
          foodEmoji: "🥕",
          requiredFeedCount: 3,
          feedCount: 0,
          habitatImage:
            "https://img.freepik.com/premium-photo/forest-scene-with-various-forest-trees-kids-story_911078-2375.jpg",
          position: 0,
          foodsEaten: [],
          steps: [],
          currentStep: 0,
        },
        {
          animalName: "Monkey",
          animalEmoji: "🐒",
          foodName: "Banana",
          foodEmoji: "🍌",
          requiredFeedCount: 2,
          feedCount: 0,
          habitatImage:
            "https://img.freepik.com/premium-vector/dense-green-tropical-forest-fresh-river_43633-12069.jpg",
          position: 0,
          foodsEaten: [],
          steps: [],
          currentStep: 0,
        },
        // Add more animals here
        {
          animalName: "Mouse",
          animalEmoji: "🐁",
          foodName: "Cheese",
          foodEmoji: "🧀",
          requiredFeedCount: 2,
          feedCount: 0,
          habitatImage:
            "https://img.freepik.com/premium-photo/forest-scene-with-various-forest-trees-kids-story_911078-2375.jpg",
          position: 0,
          foodsEaten: [],
          steps: [],
          currentStep: 0,
        },
        {
          animalName: "Dog",
          animalEmoji: "🐕",
          foodName: "Bone",
          foodEmoji: "🍖",
          requiredFeedCount: 3,
          feedCount: 0,
          habitatImage: "https://m.media-amazon.com/images/I/61LTJ027bAL.jpg",
          position: 0,
          foodsEaten: [],
          steps: [],
          currentStep: 0,
        },
      ],
      foods: [],
      currentAnimalIndex: 0,
      draggedFood: null,
      message: "",
      selectedVoice: null,
      // Added data for touch dragging
      isDragging: false,
      dragImage: "",
      dragImageStyle: {},
      gameOver: false
    };
  },
  computed: {
    currentAnimal() {
      return this.animals[this.currentAnimalIndex];
    },
    availableFoods() {
      return this.foods;
    },
  },
  mounted() {
    this.setupGame();
    this.setupVoices();
  },
  methods: {
    setupGame() {
      // Initialize foods array with multiple food items based on requiredFeedCount
      this.foods = [];
      this.animals.forEach((animal, index) => {
        // Initialize steps
        animal.steps = [];
        for (let i = 0; i < animal.requiredFeedCount; i++) {
          animal.steps.push({
            index: i,
            foodPlaced: false,
            food: null,
            foodEaten: false, // Add foodEaten property
          });
        }
        // Reset animal properties
        animal.feedCount = 0;
        animal.position = 0;
        animal.foodsEaten = [];
        animal.currentStep = 0;
        // Initialize foods
        for (let i = 0; i < animal.requiredFeedCount; i++) {
          this.foods.push({
            id: `${index}-${i}`,
            foodName: animal.foodName,
            foodEmoji: animal.foodEmoji,
          });
        }
      });
      // Shuffle foods
      this.foods = this.shuffleArray(this.foods);
      this.currentAnimalIndex = 0;
    },
    resetGame() {
      this.setupGame();
      this.message = "";
    },
    shuffleArray(array) {
      // Simple Fisher-Yates shuffle
      let currentIndex = array.length,
        temporaryValue,
        randomIndex;

      // While there remain elements to shuffle...
      while (0 !== currentIndex) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;

        // And swap it with the current element.
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
      }

      return array;
    },
    onDragStart(event, food) {
      // Handle drag start
      this.draggedFood = food;
    },
    onDropOnStep(event, step) {
      // Handle drop on the step
      if (
        this.draggedFood &&
        step.index === this.currentAnimal.currentStep &&
        this.draggedFood.foodName === this.currentAnimal.foodName
      ) {
        // Correct food and correct step
        step.foodPlaced = true;
        step.food = this.draggedFood;

        // Remove food from available foods
        this.foods = this.foods.filter(
          (food) => food.id !== this.draggedFood.id
        );

        // Play sound or give feedback
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );

        // Wait 1 second before moving the animal and making the food disappear
        setTimeout(() => {
          // Set foodEaten to true (food disappears)
          step.foodEaten = true;

          // Increment currentStep
          this.currentAnimal.currentStep++;

          // Check if all steps are completed
          if (
            this.currentAnimal.currentStep >= this.currentAnimal.steps.length
          ) {
            // Animal has reached the habitat
            // Wait 3 seconds before proceeding to next animal

            this.speakOutLoud(this.currentAnimal.animalName);
            setTimeout(() => {
              this.currentAnimalIndex++;
              if (this.currentAnimalIndex >= this.animals.length) {
                // Game over
                this.message = "🎉 All animals are home!";
                this.gameOver = true;
                this.speakOutLoud("Great job!");
                setTimeout(() => {
                  this.$router.push("/animals"); // Adjust the route as necessary
                }, 4000);
              } else {
                // Proceed to next animal, clear message (if any)
                this.message = "";
              }
            }, 3000); // Wait 3 seconds before moving to next animal
          }
        }, 1000); // Wait 1 second before moving the animal
      } else {
        // Incorrect match
        this.message = "❌";
      }
      this.draggedFood = null;
      // Clear message after 2 seconds
      setTimeout(() => {
        this.message = "";
      }, 2000);
      // Reset dragging state
      this.isDragging = false;
      this.dragImage = "";
      this.dragImageStyle = {};
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    onTouchStartFood(event, food) {
      event.preventDefault();
      const touch = event.touches[0];
      this.isDragging = true;
      this.draggedFood = food;
      this.dragImage = food.foodEmoji;
      this.dragImageStyle = {
        position: "absolute",
        left: touch.pageX + "px",
        top: touch.pageY + "px",
        pointerEvents: "none",
        fontSize: "3rem",
      };
    },
    onTouchMoveFood(event) {
      if (this.isDragging) {
        event.preventDefault();
        const touch = event.touches[0];
        this.dragImageStyle.left = touch.pageX + "px";
        this.dragImageStyle.top = touch.pageY + "px";
      }
    },
    onTouchEndFood(event) {
      if (this.isDragging) {
        event.preventDefault();
        const touch = event.changedTouches[0];
        // Check if touch ended over a step
        const steps = this.$el.querySelectorAll(".step");
        let droppedOnStep = null;
        steps.forEach((stepElement) => {
          const rect = stepElement.getBoundingClientRect();
          if (
            touch.clientX >= rect.left &&
            touch.clientX <= rect.right &&
            touch.clientY >= rect.top &&
            touch.clientY <= rect.bottom
          ) {
            // The element is under the touch point
            const stepIndex = parseInt(
              stepElement.getAttribute("data-step-index"),
              10
            );
            const step = this.currentAnimal.steps[stepIndex];
            droppedOnStep = step;
          }
        });
        if (droppedOnStep) {
          // Call onDropOnStep
          this.onDropOnStep(null, droppedOnStep);
        } else {
          // Incorrect match
          this.message = "❌";
          setTimeout(() => {
            this.message = "";
          }, 2000);
          // Reset dragging state
          this.isDragging = false;
          this.draggedFood = null;
          this.dragImage = "";
          this.dragImageStyle = {};
        }
      }
    },
  },
};
</script>

<style scoped>
.animal-habitat-game {
  text-align: center;
  width: 100%;
}

.game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.scene {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.steps-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.steps {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
}

.habitat {
  width: 80%;
  height: 200px;
  max-width: 400px;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  margin-bottom: 20px;
}

.step {
  margin: 10px 0;
  position: relative;
  width: 80px;
  height: 80px;
  border: 1px solid #ccc;
}

.step-content {
  width: 100%;
  height: 100%;
}

.placeholder {
  width: 100%;
  height: 100%;
  background-color: #eee;
}

.animal {
  position: absolute;
  bottom: -70px;
  font-size: 5rem;
  transition: all 0.5s linear;
  cursor: pointer;
}

.animal-in-habitat {
  position: absolute;
  bottom: 10px;
  left: 10px;
  font-size: 5rem;
  z-index: 10;
}

.message {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
}

.foods {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 40px 0;
  width: 100%;
}

.food {
  margin: 10px;
  cursor: grab;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.food[draggable="true"]:active {
  cursor: grabbing;
}

.dragging {
  position: absolute !important;
  z-index: 1000;
}

.drag-image {
  position: absolute;
  z-index: 1000;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

/* Removed the game-over class as it's not used */
button {
  padding: 10px 20px;
  font-size: 1rem;
  cursor: pointer;
}

/* Media query for mobile devices */
@media (max-width: 600px) {
  /* On mobile, ensure the habitat extends to 80% width */
  .habitat {
    width: 80%;
    max-width: 100%;
  }
  /* Adjust the foods section to start where the steps start */
  .foods {
    width: 100%;
  }
}
</style>
